/* eslint-disable max-lines */
import "../shadcn/index.css";
import "@vscode/codicons/dist/codicon.css";
import "react-toastify/dist/ReactToastify.css";
import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import PlaygroundRedirect from "./PlaygroundRedirect";
import * as Sentry from "@sentry/react";
import {
  getDefaultRoute,
  getTenant,
  isFreeStack,
  isInstanceStack,
  isMegaTenant,
  isSSOEnabled,
  showWorkflow,
} from "../helpers/tenant";
import {
  Expired,
  Forgot,
  FreeRegister,
  Login,
  Register,
  Reset,
  SuperTokenLogin,
  Verified,
  OktaLogin,
} from "../pages/authentication";
import {
  ApiKey,
  CompanyList,
  UserList,
  Notification,
  CostAttribution,
  ConfigureSSO,
} from "../pages/settings";
import { SSOGroupSyncSettings } from "../pages/settings/sso-group-sync";
import { AuditSettings } from "../pages/settings/audit/AuditSettings";
import { ComingSoon } from "../pages/comingSoon";
import Quickstart from "../pages/quickstart";
import { Datasets, DatasetsDetails } from "../pages/datasets";
import { DbtModels, DbtModelsDetails } from "../pages/dbtModels";
import { Incidents } from "../pages/incidents";
import { Home } from "../pages/home";
import { ContractDetails } from "../pages/contracts/ContractDetails";
import { ContractsDashboard } from "@/pages/contracts";
import {
  Datastores,
  DatastoreDetailsPage,
  DatastoreAdd,
  DbtCloudDetailsPage,
  DbtCoreDetailsPage,
} from "../pages/datastores";
import { QueryContainer } from "../pages/query";
import Security from "../pages/security/Security";
import { Credits } from "../pages/credits";
import { WarehouseDetails, Warehouses } from "../pages/warehouse";
import { SummaryV2 } from "../pages/summary";
import { Governance } from "../pages/governance";
import { DatabricksQueries, DatabricksQueryDetailPage } from "../pages/databricks/queries";
import {
  AiMlServicesSummary,
  ModelServingEndpointDetail,
  VectorSearchEndpointDetail,
  AiGatewayEndpointDetail,
} from "../pages/databricks/aiml";
import { DatabricksUsers } from "../pages/databricks/users";
import { ContactUs } from "../pages/contactUs/ContactUs";
import DatasetsList from "../pages/datasets/DatasetsList";
import { AddIntegration, IntegrationList } from "../pages/integrations";
import DbtDocsSharesList from "../pages/dbtDocs/DbtDocsSharesList";
import DbtDocsDetails from "../pages/dbtDocs/DbtDocsDetails";
import { Experimentation, ExperimentationAdd, ExperimentationItem } from "../pages/experimentation";
import { AllQueries } from "../pages/query/all-queries";
import GroupsWrapper from "../pages/query/groups/GroupsWrapper";
import { AutoTuneJobs, Jobs } from "../pages/query/Jobs";
import { RelativeComponentLoader } from "../components/loader";
import { FindInstance } from "../pages/authentication/FindInstance";
import { QueryDetails } from "../pages/query/QueryDetails";
import { GroupDetails } from "../pages/query/GroupDetails";
import { Explorer, OpportunityDetailPage } from "../pages/explorer";
import { default as Teammates } from "../pages/teammate/Teammates";
import { default as Workflow } from "../pages/workflow/Workflow";
import { default as Teammate } from "../pages/teammate/Teammate";
import { SSORegister } from "../pages/authentication/SSORegister";
import { ExportLineage, ExportLineageList } from "../pages/exportLineage";
import UserGroups from "../pages/settings/UserGroups";
import { AuthProtected, DatabricksProtected, SnowflakeProtected, TenantProtected } from "./utils";
import { ToastContainer } from "react-toastify";
import { useAppState } from "@/modules/app/useAppContext";
import { CompareRuns, RunTask } from "../pages/query/SnowflakeJobs";
import {
  DbtJob,
  DbtJobListWrapper,
  DbtExecutions,
  DbtJobModels,
  DbtJobRuns,
  DbtJobListing,
} from "../pages/query/DbtJobs";
import { SnowflakeJobSettings } from "../pages/settings/SnowflakeJob";
import Tags from "../pages/settings/tags/Tags";
import MCPIntegrations from "../pages/mcp/MCPIntegrations";
import CustomTeammate from "@/pages/teammate/CustomTeammate";
import BiReportsList from "@/pages/biReports/BiReportsList";
import BiReportDetails from "@/pages/biReports/BiReportDetails";
import BiReportsContainer from "@/pages/biReports/BiReportsContainer";
import BiUserReportsList from "@/pages/biReports/BiUserReportsList";
import BiUserReportDetail from "@/pages/biReports/BiUserReportDetail";
import { DataMatesLandingView } from "@/pages/teammate/dataMates/DataMatesLandingView";
import KnowledgeCatalogPublicDetails from "@/pages/knowledgeCatalog/KnowledgeCatalogPublicDetails";
import CustomIntegrations from "../pages/mcp/CustomIntegrations";
import { TeamSpend } from "@/pages/team-spends/TeamSpend";
import DatabricksCostBreakdown from "@/pages/databricks/cost-breakdown";
import { Clusters, ClusterDetail } from "@/pages/clusters";
import MCPMemories from "@/pages/teammate/dataMates/memory/MCPMemories";
import MCPMemoryDetails from "@/pages/teammate/dataMates/memory/MCPMemoryDetails";
import { AccountsCost } from "@/pages/accountsCost/AccountsCost";
import { isAdmin, isOwner, isServiceUser } from "@/services/auth";
import { StreamlitRoot } from "@/pages/query/streamlit/StreamlitRoot";
import { NotebooksRoot } from "@/pages/query/notebooks/NotebooksRoot";
import { StoredProceduresRoot } from "@/pages/query/stored-procedures/StoredProceduresRoot";
import { TasksRoot } from "@/pages/query/tasks/TasksRoot";
import { TasksRunHistory } from "@/pages/query/tasks/TasksRunHistory";
import { CommonJobParentWithOutlet } from "@/pages/query/common/CommonJobParentWithOutlet";
import { StreamlitRunHistory } from "@/pages/query/streamlit/StreamlitRunHistory";
import { NotebookRunHistory } from "@/pages/query/notebooks/NotebookRunHistory";
import { StoredProceduresRunHistory } from "@/pages/query/stored-procedures/StoredProceduresRunHistory";
import { StreamlitTaskHistory } from "@/pages/query/streamlit/StreamlitTaskHistory";
import { NotebooksTaskHistory } from "@/pages/query/notebooks/NotebooksTaskHistory";
import { StoredProceduresTaskHistory } from "@/pages/query/stored-procedures/StoredProceduresTaskHistory";
import { WorkflowType } from "@/pages/query/types";
import { Playground } from "@/pages/playground";
import SharedPlayground from "@/pages/playground/SharedPlayground";
import { ComputePoolDetail, ComputePoolsDashboard } from "@/pages/computePools";
import { api } from "@/helpers/api_helper";

// Knowledge catalog components - bundled together by Vite config (see vite.config.ts manualChunks)
import KnowledgeCatalog from "@/pages/knowledgeCatalog/KnowledgeCatalog";
import KnowledgeCatalogDetails from "@/pages/knowledgeCatalog/KnowledgeCatalogDetails";
import CreateUpdateKnowledgeSource from "@/pages/knowledgeCatalog/CreateUpdateKnowledgeSource";

// Tab components
import DocumentTab from "@/pages/knowledgeCatalog/tabs/DocumentTab";
import HistoryTab from "@/pages/knowledgeCatalog/tabs/HistoryTab";
import LearningsList from "@/pages/knowledgeCatalog/learnings/LearningsList";
import LearningDetailsSidebar from "@/pages/knowledgeCatalog/learnings/LearningDetailsSidebar";
import { JobDetailsWrapper } from "@/pages/query/snowflake/JobDetailsWrapper";
import { TasksDetails } from "@/pages/query/snowflake/TasksDetails";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import SubscriptionsV2 from "@/pages/subscriptions/SubscriptionsV2";
import SubscriptionNewPage from "@/pages/subscriptions/SubscriptionNewPage";
import SubscriptionDetailPage from "@/pages/subscriptions/SubscriptionDetailPage";
import { CustomWorkloadsRootWrapper } from "@/pages/query/custom-workloads/CustomWorkloadsRootWrapper";
import { CustomWorkloadsJobsWrapper } from "@/pages/query/custom-workloads/CustomWorkloadsJobsWrapper";
import { AIServicesPage, AIServiceDetailPage } from "@/pages/query/ai-services";

const QueryResult = React.lazy(() => import("../pages/queryResult/QueryResult"));
const QueryResultItem = React.lazy(() => import("../pages/queryResult/QueryResultItem"));
const DBTConfigAdd = React.lazy(() => import("../pages/dBTConfig/DBTConfigAdd"));
const DBTConfigList = React.lazy(() => import("../pages/dBTConfig/DBTConfigList"));

// Lazy load Spaces components
const Spaces = React.lazy(() => import("../pages/spaces/Spaces"));
const SpaceDetail = React.lazy(() => import("../pages/spaces/SpaceDetail"));

// Lazy load BetaFeatures component
const BetaFeatures = React.lazy(() => import("../pages/settings/betaFeatures/BetaFeatures"));

// Lazy load Internal package components - only loaded when routes are accessed
const InternalLayout = React.lazy(() =>
  import("@altimateai/internal").then(m => ({ default: m.InternalLayout }))
);
const Langfuse = React.lazy(() =>
  import("@altimateai/internal").then(m => ({ default: m.Langfuse }))
);
const Evals = React.lazy(() => import("@altimateai/internal").then(m => ({ default: m.Evals })));
const AdeBench = React.lazy(() =>
  import("@altimateai/internal").then(m => ({ default: m.AdeBench }))
);
const Rbac = React.lazy(() => import("@altimateai/internal").then(m => ({ default: m.Rbac })));
const ReferralAdmin = React.lazy(() =>
  import("@altimateai/internal").then(m => ({ default: m.ReferralAdmin }))
);

// Lazy load SQL Warehouses (Databricks)
const SqlWarehouses = React.lazy(() => import("../pages/sql-warehouses"));
const DatabricksJobs = React.lazy(() => import("@/pages/jobs"));
const JobDetailPage = React.lazy(() => import("@/pages/jobs/JobDetailPage"));
const SparkJobDetailPage = React.lazy(() => import("@/pages/jobs/SparkJobDetailPage"));
const SparkSummaryPage = React.lazy(() => import("@/pages/jobs/SparkSummaryPage"));
const RunDetailPage = React.lazy(() => import("@/pages/jobs/RunDetailPage"));
const TaskDetailPage = React.lazy(() => import("@/pages/jobs/TaskDetailPage"));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const RoutesV0 = () => {
  const tenant = getTenant();
  const ssoMode = isSSOEnabled();
  const isFreeUser = isFreeStack();
  const hasInstanceName = isInstanceStack();
  const isAdminOrOwner = isAdmin() || isOwner();
  const appState = useAppState();
  const {
    rbacEnabled,
    enableCostReports,
    isDemoTenant,
    isDatabricksTenant,
    ssoGroupSyncEnabled,
    isSparkAnalysisEnabled,
  } = appState;

  // Demo tenant - only show playground and integrations
  if (isDemoTenant) {
    return (
      <>
        <SentryRoutes>
          {/* Public routes */}
          <Route path="/demo" element={<div />} />
          <Route path="/login" element={<Login />} />
          <Route element={<AuthProtected />}>
            <Route element={<Home />}>
              <Route path="/studio" element={<Playground />} />
              <Route path="/studio/:session_id" element={<Playground />} />
              <Route path="/playground" element={<PlaygroundRedirect />} />
              <Route path="/playground/:session_id" element={<PlaygroundRedirect />} />
              <Route path="/settings/datastores" element={<Datastores />} />
              <Route path="/settings/datastores/add" element={<DatastoreAdd />} />
              <Route path="/settings/datastores/:datastoreId" element={<DatastoreDetailsPage />} />
              <Route
                path="/settings/datastores/dbt-cloud/:connectionId"
                element={<DbtCloudDetailsPage />}
              />
              <Route
                path="/settings/datastores/dbt-core/:integrationId/:environment"
                element={<DbtCoreDetailsPage />}
              />
              <Route path="*" element={<Navigate to="/studio" />} />
            </Route>
          </Route>
        </SentryRoutes>
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      <SentryRoutes>
        {/* <Route path="/test" element={null} /> */}
        <Route path="/login-redirect/okta" element={<OktaLogin />} />
        {ssoMode ? (
          // * is needed because of supertoken callback url
          <>
            <Route path="/login/*" element={<SuperTokenLogin />} />
            <Route path="/register" element={<SSORegister />} />
            <Route path="/register/:token" element={<Navigate to="/login" />} />
          </>
        ) : (
          <>
            {isFreeUser ? <Route path="/contactus" element={<ContactUs />} /> : null}
            {isFreeUser && !hasInstanceName ? (
              <Route path="/login" element={<FindInstance />} />
            ) : (
              <Route path="/login" element={<Login />} />
            )}
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/register/:token" element={<Register />} />
            <Route path="/reset/:token" element={<Reset />} />
            {isFreeUser && (
              <>
                <Route path="/register" element={<FreeRegister />} />
                <Route path="/login/:token" element={<Login />} />
                <Route path="/verified" element={<Verified />} />
              </>
            )}
          </>
        )}
        <Route path="/expired" element={<Expired />} />
        <Route
          path="kb/:id"
          element={
            <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
              <KnowledgeCatalogPublicDetails />
            </Suspense>
          }
        />
        {/* Public shared playground route - accessible without auth */}
        <Route
          path="/playground/shared/:share_id"
          element={
            <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
              <SharedPlayground />
            </Suspense>
          }
        />
        {/* Alias: backend generates /studio/shared/ URLs */}
        <Route
          path="/studio/shared/:share_id"
          element={
            <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
              <SharedPlayground />
            </Suspense>
          }
        />
        <Route element={<AuthProtected />}>
          <Route element={<Home />}>
            <Route path="/coming-soon" element={<ComingSoon />} />
            {/* Internal package routes - lazy loaded only when accessed */}
            <Route
              element={
                <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                  <InternalLayout currentTenant={tenant} apiClient={api} appState={appState} />
                </Suspense>
              }
            >
              <Route
                path="/langfuse"
                element={
                  <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                    <Langfuse />
                  </Suspense>
                }
              />
              <Route
                path="/evals"
                element={
                  <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                    <Evals />
                  </Suspense>
                }
              />
              <Route
                path="/ade-bench"
                element={
                  <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                    <AdeBench />
                  </Suspense>
                }
              />
              <Route
                path="/rbac"
                element={
                  <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                    <Rbac />
                  </Suspense>
                }
              />
              {isMegaTenant() && (
                <Route
                  path="/referrals"
                  element={
                    <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                      <ReferralAdmin />
                    </Suspense>
                  }
                />
              )}
            </Route>
            {isFreeUser && <Route path="/quickstart" element={<Quickstart />} />}
            {/* <Route path="/profile" element={<Profile />} /> */}
            <Route element={<TenantProtected tenant={tenant} />}>
              <Route element={<SnowflakeProtected />}>
                <Route path="/tables">
                  <Route index element={<DatasetsList />} />
                </Route>
                <Route path="/datasets">
                  <Route index element={<Datasets />} />
                  <Route path=":tableId" element={<DatasetsDetails />} />
                </Route>
                <Route path="/dbt_models">
                  <Route index element={<DbtModels />} />
                  <Route path=":dbtModelId" element={<DbtModelsDetails />} />
                </Route>
              </Route>
              <Route path="/experimentation">
                <Route index element={<Experimentation />} />
                <Route path="new" element={<ExperimentationAdd />} />
                <Route path=":experimentId" element={<ExperimentationItem />} />
              </Route>
              <Route path="/discover">
                <Route index element={<Explorer />} />
                <Route
                  path="opportunity/detail/:opportunityId"
                  element={<OpportunityDetailPage />}
                />
              </Route>
              <Route path="/incidents">
                <Route index element={<Incidents />} />
                <Route path=":incidentId" element={<Incidents />} />
                <Route path="event/:eventId" element={<Incidents />} />
              </Route>
              <Route element={<SnowflakeProtected />}>
                {isAdminOrOwner && (
                  <>
                    <Route path="/contracts">
                      <Route index element={<ContractsDashboard />} />
                      <Route path=":contractId" element={<ContractDetails />} />
                    </Route>
                    <Route path="/compute-pools">
                      <Route index element={<ComputePoolsDashboard />} />
                      <Route path=":poolName" element={<ComputePoolDetail />} />
                    </Route>
                  </>
                )}
              </Route>
              <Route path="/spaces" element={<Spaces />} />
              <Route path="/spaces/:id" element={<SpaceDetail />} />
              <Route path="/spaces" element={<Spaces />} />
              <Route element={<SnowflakeProtected />}>
                <Route path="/query/:queryId" element={<QueryDetails />} />
                <Route path="/query/groups/:queryHash" element={<GroupDetails />} />
                <Route path="/query" element={<QueryContainer />}>
                  <Route index element={<Navigate to="all" />} />
                  <Route path="all" element={<AllQueries />} />
                  <Route path="groups" element={<GroupsWrapper />} />
                  <Route path="jobs" element={<Jobs />}>
                    <Route index element={<Jobs />} />
                    <Route path=":jobID" element={<Jobs />} />
                  </Route>
                  <Route path="autotune_jobs" element={<AutoTuneJobs />}>
                    <Route index element={<AutoTuneJobs />} />
                    <Route path=":jobID" element={<AutoTuneJobs />} />
                  </Route>
                  <Route path="snowflake-jobs">
                    <Route
                      index
                      element={<CustomWorkloadsJobsWrapper workflowType={WorkflowType.CUSTOM} />}
                    />
                  </Route>
                  <Route path="tableau-jobs">
                    <Route
                      index
                      element={<CustomWorkloadsJobsWrapper workflowType={WorkflowType.TABLEAU} />}
                    />
                  </Route>
                  <Route path="dbt-jobs">
                    <Route index element={<DbtJobListWrapper />} />
                  </Route>
                  <Route path="streamlit" element={<StreamlitRoot />}></Route>
                  <Route path="notebooks" element={<NotebooksRoot />}></Route>
                  <Route path="stored-procedures" element={<StoredProceduresRoot />}></Route>
                  <Route
                    path="tasks"
                    element={
                      <DatatableContextProvider>
                        <TasksRoot />
                      </DatatableContextProvider>
                    }
                  ></Route>
                  <Route path="ai-services" element={<AIServicesPage />} />
                </Route>
                <Route path="/query/snowflake-jobs" element={<CustomWorkloadsRootWrapper />}>
                  <Route path=":workflowId" element={<JobDetailsWrapper />} />
                  <Route path=":workflowId/:runId" element={<RunTask />} />
                  <Route path=":workflowId/compare" element={<CompareRuns />} />
                </Route>
                <Route path="/query/tableau-jobs" element={<CustomWorkloadsRootWrapper />}>
                  <Route path=":workflowId" element={<JobDetailsWrapper />} />
                  <Route path=":workflowId/:runId" element={<RunTask />} />
                  <Route path=":workflowId/compare" element={<CompareRuns />} />
                </Route>
                <Route path="/query/dbt-jobs" element={<DbtJob />}>
                  <Route path=":workflowId" element={<DbtExecutions />}>
                    <Route path="models" element={<DbtJobModels />} />
                    <Route path="runs" element={<DbtJobRuns />} />
                    <Route path="jobs" element={<DbtJobListing />} />
                  </Route>
                </Route>
                <Route
                  path="/query/streamlit"
                  element={<CommonJobParentWithOutlet parent={"streamlit"} />}
                >
                  <Route path=":hash" element={<StreamlitRunHistory />} />
                  <Route path=":hash/:sessionId" element={<StreamlitTaskHistory />} />
                </Route>
                <Route
                  path="/query/notebooks"
                  element={<CommonJobParentWithOutlet parent={"notebooks"} />}
                >
                  <Route path=":hash" element={<NotebookRunHistory />} />
                  <Route path=":hash/:sessionId" element={<NotebooksTaskHistory />} />
                </Route>
                <Route
                  path="/query/stored-procedures"
                  element={<CommonJobParentWithOutlet parent={"stored-procedures"} />}
                >
                  <Route path=":hash" element={<StoredProceduresRunHistory />} />
                  <Route path=":hash/:queryId" element={<StoredProceduresTaskHistory />} />
                </Route>
                <Route
                  path="/query/tasks/runs/:database/:schema/:name"
                  element={
                    <DatatableContextProvider>
                      <TasksRunHistory />
                    </DatatableContextProvider>
                  }
                />
                <Route path="/query/ai-services/:serviceType" element={<AIServiceDetailPage />} />
                <Route
                  path="/query/workloads/task-details/:taskId"
                  element={
                    <DatatableContextProvider>
                      <TasksDetails />
                    </DatatableContextProvider>
                  }
                />
                <Route path="/warehouse">
                  <Route index element={<Warehouses />} />
                  <Route path=":instance_id/:warehouse_rk" element={<WarehouseDetails />} />
                </Route>
              </Route>

              {isDatabricksTenant && (
                <Route path="/sql-warehouses">
                  <Route
                    index
                    element={
                      <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                        <SqlWarehouses />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":warehouseId"
                    element={
                      <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                        <SqlWarehouses />
                      </Suspense>
                    }
                  />
                </Route>
              )}

              <Route path="/governance">
                <Route path="dbtconfig">
                  <Route
                    index
                    element={
                      <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                        <DBTConfigList />
                      </Suspense>
                    }
                  />
                  <Route
                    path="new"
                    element={
                      <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                        <DBTConfigAdd />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":id"
                    element={
                      <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                        <DBTConfigAdd />
                      </Suspense>
                    }
                  />
                </Route>
              </Route>
              <Route path="/summary">
                <Route index element={<SummaryV2 />} />
              </Route>
              <Route path="/users">
                <Route index element={isDatabricksTenant ? <DatabricksUsers /> : <Governance />} />
              </Route>
              {isDatabricksTenant && (
                <>
                  <Route path="/queries">
                    <Route index element={<DatabricksQueries />} />
                    <Route path=":queryId" element={<DatabricksQueryDetailPage />} />
                  </Route>
                  <Route path="/ai-ml">
                    <Route
                      index
                      element={
                        <DatatableContextProvider>
                          <AiMlServicesSummary />
                        </DatatableContextProvider>
                      }
                    />
                    <Route
                      path="model-serving/:endpointId"
                      element={<ModelServingEndpointDetail />}
                    />
                    <Route
                      path="vector-search/:endpointId"
                      element={<VectorSearchEndpointDetail />}
                    />
                    <Route path="ai-gateway/:endpointId" element={<AiGatewayEndpointDetail />} />
                  </Route>
                </>
              )}
              <Route path="/roles">
                <Route index element={<Governance />} />
              </Route>
              <Route element={<SnowflakeProtected />}>
                <Route path="/team-spends" element={<TeamSpend />} />
              </Route>
              <Route element={<DatabricksProtected />}>
                <Route path="/clusters" element={<Clusters />} />
                <Route path="/clusters/:clusterId" element={<ClusterDetail />} />
                <Route path="/jobs">
                  <Route
                    index
                    element={
                      <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                        <DatabricksJobs />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":jobId"
                    element={
                      <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                        <JobDetailPage />
                      </Suspense>
                    }
                  />
                  {isSparkAnalysisEnabled && (
                    <>
                      <Route
                        path=":jobId/runs/:runId"
                        element={
                          <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                            <RunDetailPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path=":jobId/tasks/:taskId"
                        element={
                          <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                            <TaskDetailPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path=":jobId/runs/:runId/tasks/:taskId"
                        element={
                          <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                            <SparkSummaryPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path=":jobId/runs/:runId/tasks/:taskId/spark-job/:sparkJobId"
                        element={
                          <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                            <SparkJobDetailPage />
                          </Suspense>
                        }
                      />
                    </>
                  )}
                </Route>
                <Route path="/cost-breakdown" element={<DatabricksCostBreakdown />} />
              </Route>
              {enableCostReports && (
                <>
                  <Route path="/subscriptions" element={<SubscriptionsV2 />} />
                  <Route path="/subscriptions/new" element={<SubscriptionNewPage />} />
                  <Route path="/subscriptions/:id/edit" element={<SubscriptionNewPage />} />
                  <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />
                </>
              )}
              {isAdminOrOwner && (
                <Route path="/configure-sso/:ssoClient" element={<ConfigureSSO />} />
              )}
              <Route element={<SnowflakeProtected />}>
                {isAdminOrOwner && <Route path="/accounts-cost" element={<AccountsCost />} />}
              </Route>
              <Route path="/collaboration/query-result">
                <Route
                  index
                  element={
                    <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                      <QueryResult />
                    </Suspense>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                      <QueryResultItem />
                    </Suspense>
                  }
                />
              </Route>
            </Route>
            {isFreeUser && (
              <Route path="/collaboration/dbt-docs">
                <Route index element={<DbtDocsSharesList />} />
                <Route path=":shareId/:conversationGroupId?" element={<DbtDocsDetails />} />
              </Route>
            )}
            <Route path="/collaboration/export-lineage">
              <Route index element={<ExportLineageList />} />
              <Route path=":itemId" element={<ExportLineage />} />
            </Route>
            <Route path="/settings">
              <Route path="tags" element={<Tags />} />
              <Route path="api-key" element={<ApiKey />} />
              <Route path="datastores" element={<Datastores />} />
              <Route path="datastores/add" element={<DatastoreAdd />} />
              <Route path="datastores/:datastoreId" element={<DatastoreDetailsPage />} />
              <Route path="datastores/dbt-cloud/:connectionId" element={<DbtCloudDetailsPage />} />
              <Route
                path="datastores/dbt-core/:integrationId/:environment"
                element={<DbtCoreDetailsPage />}
              />
              <Route path="users" element={<UserList />} />
              <Route path="companies" element={<CompanyList />} />
              <Route path="companies/:company" element={<UserList />} />
              {(!rbacEnabled || isOwner()) && (
                <Route path="integrations">
                  <Route index element={<IntegrationList />} />
                  <Route path="new" element={<AddIntegration />} />
                  <Route
                    path=":dbtCoreIntegrationId/:dbtCoreIntegrationEnv"
                    element={<IntegrationList />}
                  />
                </Route>
              )}
              <Route path="notification" element={<Notification />} />
              <Route path="snowflake-job-settings" element={<SnowflakeJobSettings />} />
              <Route path="security" element={<Security />} />
              {ssoGroupSyncEnabled && isOwner() && (
                <Route path="sso-group-sync" element={<SSOGroupSyncSettings />} />
              )}
              {ssoGroupSyncEnabled && isOwner() && (
                <Route path="audit" element={<AuditSettings />} />
              )}
              <Route path="credits" element={<Credits />} />
              <Route path="cost-attribution" element={<CostAttribution />} />
              {isServiceUser() && (
                <Route
                  path="beta-features"
                  element={
                    <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                      <BetaFeatures />
                    </Suspense>
                  }
                />
              )}
            </Route>
            <Route path="teammates" element={<Teammates />} />
            {showWorkflow() && <Route path="workflow/*" element={<Workflow />} />}
            <Route path="datamates" element={<DataMatesLandingView />} />
            <Route path="datamates/create" element={<MCPIntegrations startingStep={1} />} />
            <Route path="datamates/memories" element={<MCPMemories />} />
            <Route path="datamates/memories/:id" element={<MCPMemoryDetails />} />
            <Route
              path="datamate/edit/:teammateId"
              element={<MCPIntegrations startingStep={4} />}
            />
            <Route path="datamate/:teammateId" element={<CustomTeammate />} />
            <Route path="teammates/:teammate" element={<Teammate />} />
            <Route path="/studio" element={<Playground />} />
            <Route path="/studio/:session_id" element={<Playground />} />
            <Route path="/playground" element={<PlaygroundRedirect />} />
            <Route path="/playground/:session_id" element={<PlaygroundRedirect />} />
            <Route path="teammate/:teammateId" element={<CustomTeammate />} />
            <Route path="usergroups" element={<UserGroups />} />
            <Route path="usergroups/:id" element={<UserGroups />} />
            <Route element={<SnowflakeProtected />}>
              <Route path="bi-reports" element={<BiReportsContainer />}>
                <Route index element={<Navigate to="dashboards" replace />} />
                <Route path="dashboards" element={<BiReportsList />} />
                <Route path="users" element={<BiUserReportsList />} />
              </Route>
              <Route path="bi-reports/:id" element={<BiReportDetails />} />
              <Route path="bi-reports/users/:id" element={<BiUserReportDetail />} />
            </Route>

            <Route path="knowledge-hub">
              <Route index element={<KnowledgeCatalog />} />
              <Route path="new" element={<CreateUpdateKnowledgeSource />} />
              <Route path="edit/:id" element={<CreateUpdateKnowledgeSource />} />
              <Route path="template/:templateName" element={<KnowledgeCatalogDetails />} />
              <Route path=":id" element={<KnowledgeCatalogDetails />}>
                <Route index element={<DocumentTab />} />
                <Route path="learnings" element={<LearningsList />}>
                  <Route path=":learningId" element={<LearningDetailsSidebar />} />
                </Route>
                <Route path="history" element={<HistoryTab />} />
              </Route>
            </Route>
            <Route path="datamates/custom-integrations" element={<CustomIntegrations />} />
            {isFreeUser ? (
              <Route path="/" element={<Quickstart />}>
                {/* TODO: Remove in future */}
                <Route path="/settings/executions" element={<Navigate to="/settings/credits" />} />
              </Route>
            ) : (
              <Route path="*" element={<Navigate to={getDefaultRoute(tenant)} />} />
            )}
          </Route>
        </Route>
      </SentryRoutes>
      <ToastContainer />
    </>
  );
};

export default RoutesV0;
